/**
 * @file
 * @see https://shopify.dev/docs/api/ajax
 */

import {
  ContentTypeField,
  ServerErrorClass,
  Status,
  StatusClass,
} from "@redotech/http/semantics";
import { SetCookieField } from "@redotech/http/state";
import { ApplicationJsonType } from "@redotech/json/mime";
import axios, { AxiosHeaders, AxiosInstance } from "axios";
import setCookieParser from "set-cookie-parser";

export enum DeliveryType {
  SHIPPING = "shipping",
  IN_STORE_PICKUP = "in-store-pickup",
  UNKNOWN_OR_UNSELECTED = "unknown",
}

export interface ShopifyCart {
  attributes?: { [key: string]: string };
  items: ShopifyCartItem[];
  currency: string;
  token?: string;
  note?: string | null;
  /** Discount codes entered on the cart (Shopify Ajax API). */
  discount_codes?: { code: string; applicable: boolean }[];
  total_price?: number;
  subtotal_price?: number;
  delivery_type?: DeliveryType;
  /** Order-level coverage fee already included in `total_price` (Commerce Cloud custom-fee mode); absent on every other platform. */
  coverage_fee?: number;
}

export interface ShopifyCartItemDiscounts {
  amount: number;
  title: string;
}

export interface ShopifyCartItem {
  id: string | number;
  line_id?: string | number;
  key?: string;
  product_id: string | number;
  variant_id: string | number;
  handle: string;
  productTags?: string[] | null;
  properties?: { [key: string]: string } | null;
  quantity: number;
  vendor: string | null;
  title?: string | null;
  selling_plan?: number | null;
  selling_plan_allocation?: ShopifySellingPlanAllocation | null;
  /** Set on bundle component lines (Shopify Ajax API). */
  parent_relationship?: { parent_key: string } | null;
  /** True on bundle parent lines (Shopify Ajax API). */
  has_components?: boolean;
  discounted_price?: number;
  final_line_price?: number;
  line_price?: number;
  original_line_price?: number;
  original_price?: number;
  discounts?: ShopifyCartItemDiscounts[];
  requires_shipping: boolean;
  sku?: string | null;
  /** BigCommerce modifier selections captured for shop-on-site exchanges. */
  productOptions?: ShopifyCartItemProductOption[] | null;
}

export interface ShopifyCartItemProductOption {
  optionId: string;
  value: string;
  displayName?: string;
  displayValue?: string;
  optionType?: string;
}

export interface ShopifySellingPlanAllocation {
  compare_at_price: number;
  per_delivery_price: number;
  price: number;
  selling_plan: ShopifySellingPlan;
}
export interface ShopifySellingPlan {
  description: string | null;
  /** Absent from the Storefront GraphQL cart, which has no equivalent. */
  fixed_selling_plan?: boolean;
  id: number;
  name: string;
  recurring_deliveries: boolean;
}

export interface ShopifyCartChange {
  id?: string;
  quantity?: number;
  selling_plan?: number | null;
  line?: number | string;
  properties?: { [key: string]: string } | null;
}

export interface ShopifyCartAttributes {
  attributes: { key: string; value: string }[];
}

interface ShopifyProductImage {
  src: string;
}

export interface ShopifyProduct {
  id: number;
  tags: string;
  title: string;
  variants: ShopifyVariantJS[];
  image?: ShopifyProductImage | undefined;
}

export interface ShopifyProductJS {
  id: number | string;
  price_min: number;
  price_max: number;
  variants: ShopifyVariantJS[];
}

export interface ShopifyProducts {
  mainShopifyProduct?: ShopifyProductJS;
  returnShopifyProduct?: ShopifyProductJS;
  packageShopifyProduct?: ShopifyProductJS;
  bothShopifyProduct?: ShopifyProductJS;
}

export interface ShopifyVariantJS {
  id: number | string;
  title?: string;
  price: number;
  requires_shipping: boolean;
  compare_at_price?: string;
}

export interface ShopifyShop {
  id: string;
  metafield: ShopifyMetafield;
}

export interface ShopifyMetafield {
  id: string;
  key: string;
  namespace: string;
  value: string;
}

export interface Auth {
  storefrontDigest?: string;
}

export interface Collection {
  title: string;
  handle: string;
}

const PRIVATE_COOKIE = "storefront_digest";

/**
 * @see https://shopify.dev/docs/api/ajax#locale-aware-urls
 */
export function getShopifyRouteRoot(): string {
  return globalThis.Shopify?.routes?.root ?? "/";
}

export class ShopifyAuthError extends Error {
  override name = "ShopifyAuthError";
}

export enum ShopifyClientType {
  SHOPIFYGRAPHQL = "shopify_graphql",
  AJAX = "ajax",
  TAPCART = "tapcart",
  BIGCOMMERCE = "bigcommerce",
  COMMERCE_CLOUD = "commerce_cloud",
  LIGHTOPIA = "lightopia",
  MIVA = "miva",
}

export interface ExtensionShopifyClient {
  type: ShopifyClientType;
  cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
    preventReload?: boolean,
    coveragePrice?: number,
  ): Promise<unknown> | Promise<undefined>;
  updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal,
  ): Promise<ShopifyCart | void>;
  cartGet(signal?: AbortSignal): Promise<ShopifyCart>;
  useCachedCart?: boolean;
  consumeCartMerge?(): boolean;
  removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
    preventRefresh?: boolean,
  ): Promise<undefined>;
  updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal,
  ): Promise<undefined>;
  cartClear(): Promise<unknown>;
  getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<Collection[] | undefined>;
  productGet(handle: string, signal?: AbortSignal): Promise<ShopifyProduct>;
  productGetJS(handle: string, signal?: AbortSignal): Promise<ShopifyProductJS>;
  getShopMetafield(
    data: { key: string; namespace: string },
    signal?: AbortSignal,
  ): Promise<ShopifyShop | undefined>;
  deleteCartAttribute(
    data: { key: string },
    signal?: AbortSignal,
  ): Promise<void>;
  getProductSellingPlans?(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductSellingPlan[] | undefined>;
  applyDiscountCode?(discountCode: string, signal?: AbortSignal): Promise<void>;
  removeDiscountCodes?(signal?: AbortSignal): Promise<void>;
  updateLineItemAttributes?(
    updates: {
      lineItemId: string;
      attributes: { key: string; value: string }[];
    }[],
    signal?: AbortSignal,
  ): Promise<void>;
  /** Attach/remove a custom fee line item for Redo coverage (BigCommerce, Commerce Cloud). */
  coverageFee?: CoverageFeeCapability;
}

/** A client's ability to attach or remove a Redo coverage custom fee. */
export interface CoverageFeeCapability {
  isAvailable(signal?: AbortSignal): Promise<boolean>;
  attach(
    input: {
      productType: string;
      feeAmount: number;
      label: string;
      widgetSlug?: string;
    },
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }>;
  /** input.widgetSlug is BigCommerce-only; Commerce Cloud ignores it (fee is per cart). */
  remove(
    input?: { widgetSlug?: string },
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }>;
  /** Shows the changed fee in the page's basket; absent means a full reload is needed. */
  refreshAfterChange?(): void | Promise<void>;
}

export interface ShopifyProductSellingPlan {
  id: string;
  name: string;
}

export class ShopifyAjaxClient implements ExtensionShopifyClient {
  type = ShopifyClientType.AJAX;
  constructor(url: string, auth: Auth = {}) {
    const headers = new AxiosHeaders();
    if (auth.storefrontDigest) {
      headers.set("Cookie", `${PRIVATE_COOKIE}=${auth.storefrontDigest}`);
    }
    this.baseUrl = url;
    this.axios = axios.create({ baseURL: url, headers });
  }

  private readonly baseUrl: string;
  private readonly axios: AxiosInstance;

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   * @see https://shopify.dev/changelog/cart-line-items-now-ordered-in-reverse-chronological-order-based-on-time-of-addition
   * Adds items to the cart. New items are added to the top of the cart (default Shopify API behavior).
   * Should be used in most cases (as opposed to cartAddToEnd).
   */
  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
  ): Promise<unknown> {
    if (!params) {
      params = new URLSearchParams();
    }
    const response = await this.axios.post("cart/add.js", data, { params });
    return response.data;
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   * @see https://shopify.dev/changelog/cart-line-items-now-ordered-in-reverse-chronological-order-based-on-time-of-addition
   * There is no native Shopify API support for this, so this is a workaround.
   * This removes all items from the cart and adds them back in reverse order.
   * Use only when merchant cart's theme removes cart items by position and not by id.
   */
  async cartAddToEnd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
  ): Promise<unknown> {
    if (!params) {
      params = new URLSearchParams();
    }
    const currentCart = await this.cartGet();
    const existingItems = currentCart.items;
    const apiFormattedItems = existingItems.map(
      ({
        variant_id: id,
        quantity,
        properties,
        selling_plan_allocation: s,
      }) => ({ id, quantity, properties, selling_plan: s?.selling_plan.id }),
    );
    await this.cartClear();

    // Attempt to add all items in one request
    try {
      const allItems = { items: [...data.items, ...apiFormattedItems] };
      const response = await this.axios.post("cart/add.js", allItems, {
        params,
      });
      return response.data;

      // Attempt to add redo product first, then existing products
    } catch (error) {
      if (existingItems.length > 0) {
        try {
          const newItemsResponse = await this.axios.post("cart/add.js", data, {
            params,
          });
          const existingItemsResponse = await this.axios.post("cart/add.js", {
            items: apiFormattedItems,
          });
          return {
            ...newItemsResponse.data,
            items: [
              ...newItemsResponse.data.items,
              ...existingItemsResponse.data.items,
            ],
          };
        } catch (restoreError) {
          console.error("Failed to restore cart items:", restoreError);
          throw restoreError;
        }
      }
      throw error;
    }
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */
  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const response = await this.axios.get("cart.js", { signal });
    return response.data;
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(change),
      signal,
    });

    if (!response.ok) {
      const error: any = new Error(
        `Request failed with status code ${response.status}`,
      );
      error.response = { status: response.status, data: await response.text() };
      throw error;
    }

    return response.json();
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async updateCartAttribute(attr: ShopifyCartAttributes, signal?: AbortSignal) {
    const attributes: { [key: string]: string } = {};
    for (const a of attr.attributes) {
      attributes[a.key] = a.value;
    }

    const postData = { attributes };

    const response = await this.axios.post("cart/update.js", postData, {
      signal,
    });
    return response.data;
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    const newQuantity = item.quantity - numToRemove;
    await this.cartChange(
      { id: String(item.id), quantity: newQuantity },
      signal,
    );
  }

  async updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal,
  ): Promise<undefined> {
    await this.cartChange(
      {
        id: String(item.id),
        quantity: item.quantity,
        selling_plan: sellingPlanId,
      },
      signal,
    );
  }

  async cartClear(): Promise<unknown> {
    const response = await fetch(`${this.baseUrl}cart/clear.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      const error: any = new Error(
        `Request failed with status code ${response.status}`,
      );
      error.response = { status: response.status, data: await response.text() };
      throw error;
    }

    return response.json();
  }

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    return;
  }

  /**
   * @see https://shopify.dev/docs/api/ajax/reference/product#get-locale-products-product-handle-js
   */
  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    const response = await this.axios.get(
      `products/${encodeURIComponent(handle)}.json`,
      { signal },
    );
    return response.data.product;
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    const response = await this.axios.get(
      `products/${encodeURIComponent(handle)}.js`,
      { signal },
    );
    return response.data;
  }

  async productRecommendations(product_id: number): Promise<any> {
    const params = new URLSearchParams();
    params.append("product_id", String(product_id));
    // If this returns an HTML page asking for a password,
    // put the store's password into the storePassword field in the database
    const response = await this.axios.get("recommendations/products.json", {
      params,
    });
    return response.data;
  }

  async getStorefrontAccessTokens(accessToken: string): Promise<any> {
    const response = await this.axios.get(
      "admin/api/2026-07/storefront_access_tokens.json",
      { headers: { "X-Shopify-Access-Token": accessToken } },
    );
    return response.data;
  }

  async deleteStorefrontAccesstoken(
    tokenId: string,
    accessToken: string,
  ): Promise<any> {
    const response = await this.axios.delete(
      `admin/api/2026-07/storefront_access_tokens/${tokenId}.json`,
      { headers: { "X-Shopify-Access-Token": accessToken } },
    );
    return response.data;
  }

  async createStorefrontAccessToken(accessToken: string): Promise<any> {
    const response = await this.axios.post(
      "admin/api/2026-07/storefront_access_tokens.json",
      { storefront_access_token: { title: "Redo Storefront Access Token" } },
      {
        headers: {
          [String(ContentTypeField.name)]: String(ApplicationJsonType),
          "X-Shopify-Access-Token": accessToken,
        },
      },
    );
    return response.data;
  }

  async productsSearch(search: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("q", search);
    const response = await this.axios.get("search/suggest.json", { params });
    return response.data;
  }

  static fromEnv(auth: Auth = {}): ShopifyAjaxClient {
    return new ShopifyAjaxClient(getShopifyRouteRoot(), auth);
  }

  async getShopMetafield(): Promise<ShopifyShop | undefined> {
    return;
  }

  async deleteCartAttribute(): Promise<void> {}

  /**
   * Apply a discount code to the cart using the AJAX API
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   */
  async applyDiscountCode(
    discountCode: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.axios.post(
      "cart/update.js",
      { discount: discountCode },
      { signal },
    );
    return response.data;
  }

  /**
   * Remove all discount codes from the cart using the AJAX API
   * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   */
  async removeDiscountCodes(signal?: AbortSignal): Promise<void> {
    const response = await this.axios.post(
      "cart/update.js",
      { discount: "" },
      { signal },
    );
    return response.data;
  }
}

/**
 * @see https://help.shopify.com/en/manual/online-store/themes/password-page
 */
export async function shopifyAjaxClientPrivate(
  url: string,
  password: string,
): Promise<ShopifyAjaxClient> {
  const axios_ = axios.create({ baseURL: url });
  const data = new URLSearchParams();
  data.append("password", password);
  const headers = new AxiosHeaders();
  const response = await axios_.post("password", data.toString(), {
    headers,
    maxRedirects: 0,
    validateStatus: (status) => {
      const class_ = new Status(status, "").class();
      return !StatusClass.equals(class_, ServerErrorClass);
    },
  });
  const setCookies = response.headers[SetCookieField.name.normalized()]
    ? response.headers[SetCookieField.name.normalized()]
    : "";
  const cookies = setCookieParser(setCookies);
  const cookie = cookies.find((cookie) => cookie.name === PRIVATE_COOKIE);
  if (!cookie) {
    throw new ShopifyAuthError("Invalid store password");
  }
  return new ShopifyAjaxClient(url, { storefrontDigest: cookie.value });
}

export const getTags = (product: ShopifyProduct) => {
  return product.tags.toLowerCase().split(/, */).filter(Boolean);
};
